import React from 'react';
import { Box, Container, Grid, Typography, useTheme, Chip } from '@mui/material';
import TopUtilityBar from '@/components/sections/TopUtilityBar';
import HeaderNav from '@/components/sections/HeaderNav';
import FooterSection from '@/components/sections/FooterSection';
import ClientLogosSection from '@/components/sections/ClientLogosSection';
import RFQFormSection from '@/components/sections/RFQFormSection';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';

export const IndustriesPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const sectors = [
    {
      title: 'Real Estate & Residential Developers',
      icon: 'domain',
      badge: 'RESIDENTIAL TOWNSHIPS',
      clients: 'Brigade Group, Lodha Group, Embassy Group, Provident Housing, Alembic Group, Confident Group, Century Real Estate',
      offerings: [
        'Exterior curtain walling & ACP cladding',
        'Frameless swimming pool glass railings',
        'High-spec aluminium doors & windows',
        'Grand entrance glass canopies',
        'Underground parking traffic safety systems',
      ],
      valueAdd: 'In-house 7,000 sq.ft yard guarantees schedule control; single-source accountability for all external packages.',
    },
    {
      title: 'Commercial & Corporate IT Parks',
      icon: 'business_center',
      badge: 'COMMERCIAL IT PARKS',
      clients: 'SAP Labs India, Corporate Office Developers',
      offerings: [
        'High-performance DGU structural glazing',
        'Acoustic glass interior partitions & shopfronts',
        'Structural steel entry canopies',
        'Automatic boom barriers & access control',
        'Hot-applied thermoplastic road markings',
      ],
      valueAdd: 'High energy-efficiency glazing reducing HVAC operational costs; zero-disruption site installation.',
    },
    {
      title: 'Main EPC Contractors & General Contractors',
      icon: 'engineering',
      badge: 'TURNKEY SUB-CONTRACTING',
      clients: 'Tier-1 EPC Partners & Main Civil Contractors',
      offerings: [
        'Turnkey sub-contracted building envelope packages',
        'Structural steel pre-assembly (50 MT / month)',
        'Off-site dimensional inspection & QC',
        'Integrated site erection management',
      ],
      valueAdd: 'Complete documentation packs (MTCs, warranties), ISO 9001/14001/45001 compliance, strict Gantt schedule alignment.',
    },
    {
      title: 'Infrastructure & Public Sector',
      icon: 'subway',
      badge: 'PUBLIC INFRASTRUCTURE',
      clients: 'BMRCL (Bangalore Metro Rail), Brigade Foundation / Public Infrastructure',
      offerings: [
        'Civil execution & metro corridor works',
        'Thermoplastic retro-reflective road markings',
        'Automatic boom barriers & W-beam crash barriers',
        'Public memorial & monument steel structures',
      ],
      valueAdd: 'Full compliance with IRC, MoRTH, and BIS safety standards.',
    },
  ];

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', color: theme.palette.text.primary, transition: 'background-color 0.3s ease, color 0.3s ease' }}>
      <TopUtilityBar />
      <HeaderNav />

      {/* Hero Banner */}
      <Box sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={7}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.08)',
                  border: `1px solid ${isDark ? 'rgba(0, 102, 255, 0.3)' : 'rgba(0, 80, 203, 0.2)'}`,
                  mb: 3,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: theme.palette.primary.main }}>
                  business
                </span>
                <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  INDUSTRIES WE SERVE
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '3.25rem', md: '3.75rem' },
                  color: theme.palette.text.primary,
                  fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: '0.01em',
                  textTransform: 'uppercase',
                  mb: 3,
                }}
              >
                SPECIALIZED SOLUTIONS FOR<br />
                <Box component="span" sx={{ color: theme.palette.primary.main }}>
                  DIVERSE BUILT ENVIRONMENTS.
                </Box>
              </Typography>

              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.7, mb: 4, maxWidth: 600 }}>
                Whether developing commercial IT parks, luxury residential townships, public infrastructure, or institutional facilities, Inverika provides tailored engineering and execution capabilities.
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  document.getElementById('sectors')?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{ textTransform: 'uppercase', fontWeight: 700, fontSize: '0.8125rem', px: 3, py: 1.25 }}
              >
                EXPLORE SECTORS ↓
              </Button>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={{ p: 1, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', height: { xs: 320, md: 440 } }}>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200&auto=format&fit=crop"
                  alt="Commercial Built Environment"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1 }}
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sectors Breakdown */}
      <Box id="sectors" sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
              PROJECT ENVIRONMENTS
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 700 }}>
              Tailored Sector Execution
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {sectors.map((sector, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justify: 'space-between',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 32, color: theme.palette.primary.main }}>
                        {sector.icon}
                      </span>
                      <Chip label={sector.badge} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.1)', color: theme.palette.primary.main }} />
                    </Box>

                    <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: 'Space Grotesk', color: theme.palette.text.primary, mb: 2 }}>
                      {sector.title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 600, fontSize: '0.85rem', mb: 3 }}>
                      Key Partners: {sector.clients}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 1 }}>
                      Core Offerings:
                    </Typography>

                    <Box component="ul" sx={{ pl: 2, m: 0, mb: 3 }}>
                      {sector.offerings.map((offering, i) => (
                        <Typography component="li" variant="body2" key={i} sx={{ color: theme.palette.text.secondary, mb: 0.75, fontSize: '0.875rem' }}>
                          {offering}
                        </Typography>
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 600, display: 'block', fontStyle: 'italic' }}>
                      Strategic Value Add: {sector.valueAdd}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Client Logos Section */}
      <ClientLogosSection />

      {/* RFQ Form */}
      <RFQFormSection />

      {/* Footer */}
      <FooterSection />
    </Box>
  );
};

export default IndustriesPage;
